import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { CashFlow } from '@api/CashFlows/CashFlow';
import RecurringExpenseServ from '@api/RecurringExpenses/RecurringExpenseServ';
import { currencyFormatter } from '@utils/Numbers';
import { formatPeriod, PAID_FROM_CASH_LABELS } from '@utils/Expenses';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { isActiveCashRegister } from '@utils/CashRegisters';

interface ModalConfirmExpenseProps {
    expense?: CashFlow;
    onClose: () => void;
    onConfirmed: () => void;
}

/**
 * Confirmar el monto de un gasto fijo que el aseo generó "por confirmar" (plan de
 * Gastos §8.5): se escribe el monto del recibo (o se deja el mismo). Mientras nadie
 * confirme, el Estado de Resultados lo cuenta como "estimado" y no resta en el corte.
 */
const ModalConfirmExpense: React.FunctionComponent<ModalConfirmExpenseProps> = ({ expense, onClose, onConfirmed }) => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const { cashRegister, branchOffices } = useSelector((state: RootReducer) => state.krispis);
    const [amount, setAmount] = useState('');
    const [paidFromCash, setPaidFromCash] = useState(false);

    useEffect(() => {
        setAmount(expense ? String(expense.amount ?? '') : '');
        setPaidFromCash(!!expense?.paidFromCashOnConfirm);
    }, [expense]);

    const canUseCash =
        !!expense?.branchOfficeId &&
        expense.branchOfficeId === branchOfficeSelected?.id &&
        isActiveCashRegister(cashRegister, expense.branchOfficeId);
    const branchName = branchOffices.find((branch) => branch.id === expense?.branchOfficeId)?.name || '';

    const confirmWith = async (fromCash: boolean) => {
        if (!expense) return;
        showLoader();
        try {
            await RecurringExpenseServ.confirmInstance(expense, Number(amount), fromCash);
            showAlertMessage('Gasto confirmado');
            onConfirmed();
            onClose();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    /**
     * Un fijo "de la caja" pregunta antes (la plantilla puede traerlo encendido desde que
     * se capturó): la renta casi nunca sale de la caja de un turno, y si se confirma así
     * el corte de hoy espera miles de pesos menos. Pregunta, nunca bloquea.
     */
    const onConfirm = () => {
        if (!paidFromCash) {
            void confirmWith(false);
            return;
        }
        showIOSAlert({
            title: '¿Salió de la caja de este turno?',
            message: `El corte de hoy va a esperar ${currencyFormatter(Number(amount))} menos.\n\nSi se pagó con otro dinero (transferencia, efectivo guardado de otros días, dinero del dueño), elige "Fue con otro dinero" para que el corte no salga descuadrado.`,
            buttons: [
                {
                    text: 'Fue con otro dinero',
                    onPress: () => {
                        setPaidFromCash(false);
                        void confirmWith(false);
                    }
                },
                { text: 'Sí, salió de la caja', onPress: () => void confirmWith(true) },
                { text: 'Revisar', style: 'cancel', onPress: () => {} }
            ]
        });
    };

    return (
        <Dialog open={!!expense} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ style: { borderRadius: 15, padding: 4 } }}>
            <DialogTitle fontWeight={600}>Confirmar monto</DialogTitle>
            <DialogContent>
                {expense && (
                    <Stack spacing={2} pt={1}>
                        <Typography>
                            {`${expense.categoryName || 'Gasto'}${expense.period ? ` de ${formatPeriod(expense.period)}` : ''}${
                                branchName ? ` · ${branchName}` : ''
                            }`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {`Estimado con el último monto: ${currencyFormatter(expense.amount)}. Escribe el del recibo.`}
                        </Typography>
                        <TextField
                            autoFocus
                            label="Monto del recibo"
                            type="number"
                            value={amount}
                            onChange={(event) => setAmount(event.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            inputProps={{ min: 0, step: '0.01' }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={paidFromCash}
                                    disabled={!canUseCash && !paidFromCash}
                                    onChange={(event) => setPaidFromCash(event.target.checked)}
                                />
                            }
                            label={paidFromCash ? PAID_FROM_CASH_LABELS.fromCash : PAID_FROM_CASH_LABELS.otherMoney}
                        />
                        <Typography variant="caption" color="text.secondary" mt="-8px !important">
                            {PAID_FROM_CASH_LABELS.hint}
                        </Typography>
                        {paidFromCash && !canUseCash && (
                            <Alert severity="warning">
                                Para sacarlo de la caja de un turno hay que confirmarlo desde la sucursal, con su caja abierta.
                            </Alert>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Volver</Button>
                <Button variant="contained" disabled={!(Number(amount) > 0)} onClick={onConfirm}>
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalConfirmExpense;
